import React, { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, Clock, Send, CheckCircle2, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { businessInfo } from "../data/mock";
import { toast } from "sonner";
import { createInquiry } from "../lib/db";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    pickupAddress: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.phone || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      await createInquiry({
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        pickup_address: formData.pickupAddress,
        message: formData.message,
      });
      setSubmitted(true);
      toast.success("Your inquiry has been submitted successfully!");
    } catch (err) {
      toast.error("Failed to submit inquiry. Please try again.");
    }
  };

  return (
    <div>
      {/* Page Header */}
      <section className="bg-brand-dark py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-brand-light uppercase tracking-wider mb-2">Get in Touch</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "'Lora', serif" }}>Contact Us</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-base">Have a question or want to book a pickup? Reach out to us through any of the options below.</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Lora', serif" }}>Contact Information</h2>
              <div className="space-y-5">
                <a href={`tel:${businessInfo.phone}`} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-lg bg-brand-lightest flex items-center justify-center shrink-0 group-hover:bg-brand transition-colors">
                    <Phone className="w-5 h-5 text-brand group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Call Us</p>
                    <p className="text-sm text-gray-500">{businessInfo.phone}</p>
                  </div>
                </a>
                <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-lg bg-green-50 flex items-center justify-center shrink-0 group-hover:bg-[#25D366] transition-colors">
                    <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">WhatsApp</p>
                    <p className="text-sm text-gray-500">{businessInfo.whatsapp}</p>
                  </div>
                </a>
                <a href={`mailto:${businessInfo.email}`} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-lg bg-brand-lightest flex items-center justify-center shrink-0 group-hover:bg-brand transition-colors">
                    <Mail className="w-5 h-5 text-brand group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Email</p>
                    <p className="text-sm text-gray-500">{businessInfo.email}</p>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-brand-lightest flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-brand" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Address</p>
                    <p className="text-sm text-gray-500">{businessInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-brand-lightest flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-brand" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Working Hours</p>
                    <p className="text-sm text-gray-500">Mon – Sat: 9:00 AM – 8:00 PM</p>
                    <p className="text-sm text-gray-500">Sunday: Closed</p>
                  </div>
                </div>
                <a href={`https://${businessInfo.website}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-lg bg-brand-lightest flex items-center justify-center shrink-0 group-hover:bg-brand transition-colors">
                    <Globe className="w-5 h-5 text-brand group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Website</p>
                    <p className="text-sm text-gray-500">{businessInfo.website}</p>
                  </div>
                </a>
              </div>

              <Separator className="my-6" />

              <div className="flex gap-3">
                <Button asChild className="bg-brand hover:bg-brand-dark text-white font-semibold flex-1">
                  <a href={`tel:${businessInfo.phone}`}>
                    <Phone className="w-4 h-4" />
                    Call Now
                  </a>
                </Button>
                <Button asChild className="bg-[#25D366] hover:bg-[#1da851] text-white font-semibold flex-1">
                  <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border shadow-lg">
                <CardContent className="p-6 md:p-8">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>Inquiry Submitted!</h3>
                      <p className="text-gray-500 mb-6">Thank you for reaching out. We'll get back to you shortly.</p>
                      <Button
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ fullName: "", phone: "", email: "", pickupAddress: "", message: "" });
                        }}
                        variant="outline"
                        className="border-brand text-brand hover:bg-brand hover:text-white"
                      >
                        Submit Another Inquiry
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Lora', serif" }}>Send Us an Inquiry</h3>
                      <p className="text-sm text-gray-500 mb-6">Fill in the form below and we'll respond as soon as possible.</p>
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <Label htmlFor="fullName" className="text-sm font-medium text-gray-700 mb-1.5 block">Full Name <span className="text-red-500">*</span></Label>
                            <Input
                              id="fullName"
                              name="fullName"
                              value={formData.fullName}
                              onChange={handleChange}
                              placeholder="Enter your full name"
                              className="border-gray-300 focus:border-brand focus:ring-brand"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-1.5 block">Phone Number <span className="text-red-500">*</span></Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="Enter your phone number"
                              className="border-gray-300 focus:border-brand focus:ring-brand"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-1.5 block">Email Address</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email (optional)"
                            className="border-gray-300 focus:border-brand focus:ring-brand"
                          />
                        </div>
                        <div>
                          <Label htmlFor="pickupAddress" className="text-sm font-medium text-gray-700 mb-1.5 block">Pickup Address</Label>
                          <Textarea
                            id="pickupAddress"
                            name="pickupAddress"
                            value={formData.pickupAddress}
                            onChange={handleChange}
                            placeholder="Enter pickup address (optional)"
                            rows={2}
                            className="border-gray-300 focus:border-brand focus:ring-brand resize-none"
                          />
                        </div>
                        <div>
                          <Label htmlFor="message" className="text-sm font-medium text-gray-700 mb-1.5 block">Message / Inquiry <span className="text-red-500">*</span></Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Describe your courier requirement..."
                            rows={4}
                            className="border-gray-300 focus:border-brand focus:ring-brand resize-none"
                          />
                        </div>
                        <Button type="submit" className="bg-brand hover:bg-brand-dark text-white font-semibold w-full h-11" size="lg">
                          <Send className="w-4 h-4" />
                          Submit Inquiry
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: "'Lora', serif" }}>Find Us on the Map</h2>
          <div className="rounded-xl overflow-hidden shadow-lg border h-[350px] md:h-[450px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.5!2d77.155!3d28.631!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d038b3b0b3b3b%3A0x3b3b3b3b3b3b3b3b!2sMansarovar%20Garden%2C%20New%20Delhi%2C%20Delhi%20110015!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Om Sai Express Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
